import { useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant, TextArea } from "@next-platform/client-common-spicy-ui";
import { TFunction } from "i18next";
import { useCallback } from "react";

import { Col } from "@/components/customComponents";
import { getVoicePreviewButtonIcon } from "@/utils/getVoicePreviewButtonProps";

import Trans from "./Trans/Trans";

const DEFAULT_SAMPLE_TEXT = "Write something to say....";
const ICON_CLASSNAMES = { icon: "fill-white" };

export interface IGenerateSampleSectionProps {
  sampleText: string;
  isPlaying: boolean;
  isLoading?: boolean;
  onSpeak: () => void;
  onStop: () => void;
  disabled?: boolean;
}

const resolvePreviewAriaLabel = ({ isLoading, isPlaying, t }: { isLoading: boolean; isPlaying: boolean; t: TFunction }) => {
  if (isLoading) {
    return t("voice:form.sample.aria.generating");
  }
  if (isPlaying) {
    return t("voice:form.sample.aria.stopPreview");
  }
  return t("voice:form.sample.aria.playPreview");
};

export function GenerateSampleSection({ sampleText, isPlaying, isLoading = false, onSpeak, onStop, disabled = false }: IGenerateSampleSectionProps) {
  const { t } = useTranslation();
  const handleButtonClick = useCallback(() => {
    if (isPlaying || isLoading) {
      onStop();
    } else {
      onSpeak();
    }
  }, [isPlaying, isLoading, onStop, onSpeak]);

  const PreviewCta = () => {
    if (isLoading) {
      return <Trans i18nKey="voice:form.sample.cta.generating" />;
    }
    if (isPlaying) {
      return <Trans i18nKey="voice:form.sample.cta.stop" />;
    }
    return <Trans i18nKey="voice:form.sample.cta.speak" />;
  };

  const previewAriaLabel = resolvePreviewAriaLabel({ isLoading, isPlaying, t });
  return (
    <Col className="gap-md">
      <TextArea
        label={t("voice:form.preview.sample.text.title")}
        className="!opacity-100"
        disabled={true}
        placeholder={DEFAULT_SAMPLE_TEXT}
        value={sampleText}
        rows={4}
        maxRows={8}
        aria-label="sample-text"
      />
      <Button
        variant={ButtonVariant.DARK}
        size={ButtonSize.LARGE}
        onClick={handleButtonClick}
        color={ButtonColorVariant.DEFAULT}
        disabled={disabled || !sampleText.trim()}
        loading={isLoading}
        iconStart={getVoicePreviewButtonIcon(isLoading, isPlaying)}
        classNames={ICON_CLASSNAMES}
        aria-label={previewAriaLabel}
      >
        <PreviewCta />
      </Button>
    </Col>
  );
}
